import { Building2, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { api, type CustomersView } from '../api';
import { paletteCommands } from '../navigation';
import {
  customerPaletteHits,
  customersSearchPath,
  filterByQuery,
  paletteSearchDebounceMs,
  paletteSearchMinChars,
  type PaletteHit,
} from '../lib/palette-search';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from './ui/command';
import { askShortcutLabel } from '../amy/shortcut';

/**
 * ⌘K desk palette: jump to a page, search customers by name, or hand off to Ask (⌘J).
 * Invoice number search waits on a list filter; customer search uses GET /app-api/customers?q=.
 */
export function CommandPalette({
  open,
  onOpenChange,
  roles,
  demo,
  modules,
  persona,
  onAsk,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: readonly string[];
  demo: boolean;
  modules?: { credit?: boolean; simple?: boolean };
  persona: string;
  onAsk: () => void;
}) {
  const commands = paletteCommands(roles, demo, modules, persona);
  const pages = commands.filter(c => c.href);
  const ask = commands.find(c => c.action === 'ask');
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<PaletteHit[]>([]);
  const [searching, setSearching] = useState(false);
  const visiblePages = useMemo(() => filterByQuery(pages, query), [pages, query]);

  useEffect(() => {
    if (!open) { setQuery(''); setHits([]); setSearching(false); }
  }, [open]);

  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < paletteSearchMinChars) {
      setHits([]);
      setSearching(false);
      return;
    }
    let active = true;
    setSearching(true);
    const timer = window.setTimeout(() => {
      void api<CustomersView>(customersSearchPath(q))
        .then(view => { if (active) setHits(customerPaletteHits(view.rows)); })
        .catch(() => { if (active) setHits([]); })
        .finally(() => { if (active) setSearching(false); });
    }, paletteSearchDebounceMs);
    return () => { active = false; window.clearTimeout(timer); };
  }, [query, open]);

  function run(href?: string, action?: 'ask') {
    onOpenChange(false);
    if (action === 'ask') { onAsk(); return; }
    if (href) window.location.hash = href;
  }

  const empty = !visiblePages.length && !hits.length && !ask;
  const emptyLabel = searching ? 'Searching customers…'
    : query.trim().length >= paletteSearchMinChars ? 'No customers or pages match.'
      : 'Nothing matches.';

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Command palette"
      description="Search customers, jump to a page, or ask the agent."
    >
      <Command shouldFilter={false}>
        <CommandInput
          placeholder="Search customers or go to a page…"
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {empty && <CommandEmpty>{emptyLabel}</CommandEmpty>}
          {hits.length > 0 && (
            <CommandGroup heading="Customers">
              {hits.map(hit => (
                <CommandItem
                  key={hit.id}
                  value={`${hit.label} ${hit.keywords.join(' ')}`}
                  onSelect={() => run(hit.href)}
                >
                  <Building2 aria-hidden />
                  <span className="palette-hit-label">{hit.label}</span>
                  {hit.hint && <CommandShortcut>{hit.hint}</CommandShortcut>}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {visiblePages.length > 0 && (
            <CommandGroup heading="Go to">
              {visiblePages.map(item => (
                <CommandItem
                  key={item.id}
                  value={`${item.label} ${item.keywords.join(' ')}`}
                  onSelect={() => run(item.href)}
                >
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {ask && <>
            <CommandSeparator />
            <CommandGroup heading="Agent">
              <CommandItem
                value={`${ask.label} ${ask.keywords.join(' ')}`}
                onSelect={() => run(undefined, 'ask')}
              >
                <Sparkles aria-hidden />
                {ask.label}
                <CommandShortcut>{askShortcutLabel}</CommandShortcut>
              </CommandItem>
            </CommandGroup>
          </>}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
